// Fetches USD-based exchange rates, trying the primary CDN then a fallback mirror.
const RATES_ENDPOINTS = [
  'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json',
  'https://latest.currency-api.pages.dev/v1/currencies/usd.json',
];

const CACHE_KEY = 'rs_currency_rates_usd';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rates || Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    return parsed.rates;
  } catch (e) {
    return null;
  }
}

function writeCache(rates) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ rates, timestamp: Date.now() }));
  } catch (e) {
    // Ignore storage failures (private browsing, quota, etc.)
  }
}

async function fetchFromEndpoint(endpoint) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 6000);
  try {
    const response = await fetch(endpoint, { signal: controller.signal });
    if (!response.ok) {
      console.warn(`[currency] ${endpoint} responded with ${response.status}`);
      return null;
    }
    const data = await response.json();
    const rates = data?.usd;
    if (!rates || typeof rates !== 'object') {
      console.warn(`[currency] ${endpoint} returned an unexpected shape:`, data);
      return null;
    }
    return rates;
  } catch (e) {
    console.warn(`[currency] fetch failed for ${endpoint}:`, e);
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

let inFlightPromise = null;

/**
 * Returns a map of lowercase ISO-4217 currency code -> rate relative to 1 USD,
 * e.g. { eur: 0.92, gbp: 0.79, ... }. Tries each endpoint in RATES_ENDPOINTS
 * in order until one succeeds. Returns null if all endpoints fail.
 */
export async function fetchUsdRates() {
  if (typeof window === 'undefined') return null;

  const cached = readCache();
  if (cached) return cached;

  if (inFlightPromise) return inFlightPromise;

  inFlightPromise = (async () => {
    for (const endpoint of RATES_ENDPOINTS) {
      const rates = await fetchFromEndpoint(endpoint);
      if (rates) {
        writeCache(rates);
        return rates;
      }
    }
    console.warn('[currency] rate fetch failed on all endpoints; defaulting to USD.');
    return null;
  })();

  try {
    return await inFlightPromise;
  } finally {
    inFlightPromise = null;
  }
}

export default fetchUsdRates;
